/**
 * Minimal, dependency-free HTML sanitizer for the self-built rich-text editor.
 *
 * Parses the dirty string with DOMParser, then walks the tree keeping only an
 * allowlist of tags. Unknown tags are UNWRAPPED (their text + allowed children
 * are kept); script/style are dropped entirely. Every attribute is stripped
 * except `href` on anchors, which must be an http(s)/mailto URL - and every
 * surviving anchor is forced to open safely in a new tab.
 *
 * This is intentionally small: the only HTML that ever reaches it is produced
 * by our own contentEditable + execCommand, so we don't need to defend against
 * the full XSS surface a general-purpose library would. We still treat the
 * input as hostile (it round-trips through the DB and renders elsewhere).
 */

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'H2', 'H3',
  'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE',
]);

// Tags whose entire subtree is discarded (not unwrapped).
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'HEAD', 'META', 'LINK', 'TITLE']);

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

function cleanNode(node: Node, out: Node, doc: Document): void {
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(doc.createTextNode(child.textContent ?? ''));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const el = child as Element;
    const tag = el.tagName.toUpperCase();

    if (DROP_TAGS.has(tag)) return;

    if (ALLOWED_TAGS.has(tag)) {
      const fresh = doc.createElement(tag.toLowerCase());
      if (tag === 'A') {
        const href = el.getAttribute('href')?.trim() ?? '';
        if (SAFE_HREF.test(href)) {
          fresh.setAttribute('href', href);
          fresh.setAttribute('target', '_blank');
          fresh.setAttribute('rel', 'noopener noreferrer');
        }
      }
      cleanNode(el, fresh, doc);
      out.appendChild(fresh);
    } else {
      // Unknown but harmless wrapper (div, span, font, …): unwrap, keep kids.
      cleanNode(el, out, doc);
    }
  });
}

export function sanitizeHtml(dirty: string): string {
  if (!dirty) return '';
  const doc = new DOMParser().parseFromString(dirty, 'text/html');
  const container = doc.createElement('div');
  cleanNode(doc.body, container, doc);
  return container.innerHTML;
}

/** Strip every tag, returning the plain text - used to test "is this empty?". */
export function htmlToText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/ /g, ' ').trim();
}
